'use client';
import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';

export default function About() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setVisible(true);},
      { threshold: 0.15 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="about" className="w-full bg-white py-20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header with background */}
        <div className="relative rounded-2xl overflow-hidden mb-16">
          {/* Background image */}
          <div
            className="absolute inset-0 w-full h-full"
            style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=1400&q=80')",
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-brand-navy/75" />
          {/* Content */}
          <div className="relative z-10 text-center py-16 px-6">
            <h2 className="font-serif text-4xl md:text-5xl font-medium text-white mb-4">
              Grid Global Commercial Brokerage LLC
            </h2>
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="h-px w-16 bg-brand-green" />
              <div className="w-2 h-2 rounded-full bg-brand-green" />
              <div className="h-px w-16 bg-brand-green" />
            </div>
            <p className="font-sans text-white/80 text-lg max-w-2xl mx-auto">
              A trusted partner in commercial finance and brokerage services
            </p>
          </div>
        </div>

        {/* Content Grid */}
        <div ref={ref} className="grid lg:grid-cols-9 gap-12 items-center">
          {/* Text - 5 cols */}
          <div
            className={`lg:col-span-5 transition-all duration-700 ${
            visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`
            }>
            
            <h1 className="font-serif text-3xl font-medium text-brand-navy mb-5">
              About Grid Global
            </h1>
            <p className="font-sans text-gray-600 text-base leading-relaxed mb-5">
              Grid Global Commercial Brokerage LLC is a premier financial services firm specializing in commercial brokerage, loan origination, and strategic financial consulting. We bridge the gap between businesses seeking capital and the financial institutions that provide it, delivering tailored solutions that drive growth and success.
            </p>
            <p className="font-sans text-gray-600 text-base leading-relaxed mb-8">
              With a seasoned team of financial professionals, we offer comprehensive services including commercial loan management, financial advisory, and brokerage solutions. Our commitment to excellence and client-first approach has made us a trusted partner for businesses across diverse industries.
            </p>

            {/* Key Points */}
            <div className="grid sm:grid-cols-2 gap-4">
              {[
              { title: 'Licensed & Regulated', desc: 'Fully licensed commercial brokerage operating under strict regulatory standards' },
              { title: 'Expert Team', desc: 'Seasoned professionals with decades of combined financial industry experience' },
              { title: 'Tailored Solutions', desc: 'Customized financial strategies designed to meet your unique business needs' },
              { title: 'Global Network', desc: 'Access to an extensive network of lenders, investors, and financial partners' }]?.
              map((item) =>
              <div key={item?.title} className="flex gap-3 p-4 rounded-lg bg-gray-50 hover:bg-brand-navy/5 transition-colors group">
                  <div className="w-5 h-5 rounded-full bg-brand-green flex-shrink-0 mt-0.5 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-sans font-semibold text-brand-navy text-sm mb-1">{item?.title}</div>
                    <div className="font-sans text-gray-500 text-xs leading-relaxed">{item?.desc}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Image - 4 cols */}
          <div
            className={`lg:col-span-4 transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`
            }>
            
            <div className="relative">
              {/* Main image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-brand-navy/20">
                <Image
                  src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80"
                  alt="Modern commercial office building representing Grid Global's professional financial services"
                  width={600}
                  height={450}
                  className="w-full h-80 object-cover" />
                
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/60 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-brand-green/20 flex items-center justify-center">
                        <svg className="w-5 h-5 text-brand-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-sans font-bold text-brand-navy text-sm">Trusted Since 2009</div>
                        <div className="font-sans text-gray-500 text-xs">15+ years of excellence in commercial finance</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}